import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { environment } from '../config/editor-environment';
import { ManufacturingMethod, ProjectType, RunType } from '../models/enums';
import { DEFAULT_MOLD_CONFIG, DEFAULT_SOFT_JAWS_CONFIG, Project } from '../models/interfaces';
import { LoadCaseItem } from '../models/loadcase.model';
import { Material } from '../models/material.model';
import { ProgressService } from './progress.service';
import { normalizeToNewShape } from './project-normalization.util';
import {
  captureActiveCaseFromFlat,
  projectFlatFromCase,
  computeCaseLabel,
  makeDefaultCase,
  canAddMethod,
  FlatManufacturingFields,
} from './case-sync.util';

@Injectable({
  providedIn: 'root'
})
export class ProjectConfigurationService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private progressService = inject(ProgressService);

  public project_id?: string | null;
  public other_user_id?: string | null;

  // Example project state
  public isExampleProject = false;
  public copyingExampleProject = false;

  public projectImages: {
    image?: string;
    image_optimized?: string;
    image_optimized_parametric?: string;
    image_fea_stress?: string;
    image_fea_displacement?: string;
  } = {};

  public project: Project = {
    name: '',
    materials: [],
    status: 1,
    projectType: ProjectType.TopologyOptimization,
    optimization: undefined,
    parts: {
      design: [],
      nondesign: []
    },
    loadcases: [],
    goal: {
      type: 'Generative Design Exploration',
      objective: { type: 'Min', responseId: 'compliance-response' },
      fractionOfMaterial: 30,
      massTarget: 0,
      frequencyConstraint: 200,
      constraints: [],
      responses: []
    },
    resolution: 'Low',
    symmetry: { xy: false, xz: false, yz: false },
    manufacturingMethod: ManufacturingMethod.AdditiveManufacturing,
    millingVector: { xp: false, yp: false, zp: false, xm: false, ym: false, zm: false },
    drawDirectionVector: { xp: false, yp: false, zp: false, xm: false, ym: false, zm: false },
    ribsDensity: 'Low',
    minimumFeatureSize: 0,
    minimumToolSize: 0,
    minimumToolSizeUnit: 'mm',
    wallThickness: 10,
    bottomCutFrac: 0.6,
    userCoordinateSystem: {
      origin: [0, 0, 0],
      rotation: [0, 0, 0],
      enabled: false
    },
    generateOrganic: true,
    generatePrismatic: false,
    skeletonMethod: 'Skeleton3D',
    stressConstraintEnabled: true,
    deformationConstraintEnabled: false,
    flowVersion: 1,
    runType: RunType.Standard,
    softJaws: DEFAULT_SOFT_JAWS_CONFIG,
    mold: DEFAULT_MOLD_CONFIG,
    userDefinedResolution: { enabled: false, value: 600000 },
    solverParameters: {
      static: { tolerance: 0.0001, maxIterations: 150 },
      eigenvalue: { tolerance: 0.0001, maxIterations: 150 },
    }
  };

  public downloadLinks: any;
  /** Persisted BDF state from findOneForGUI; null on masters / projects without BDF. */
  public bdfJob: {
    status: string;
    s3Key?: string;
    localPath?: string;
    filename?: string;
    source?: 'organic' | 'prismatic' | 'analysis';
    progress?: number;
    stage?: string;
  } | null = null;
  public softJawsResultUrl?: string;

  public sharing: any;
  public isViewOnlyPermission = () => this.sharing?.permission === 0;
  public isEditablePermission = () => this.sharing?.permission === 1 || !this.sharing;
  public isSoftJawsProject = () => this.project.projectType === ProjectType.SoftJaws;
  public isMoldProject = () => this.project.projectType === ProjectType.Mold;

  private projectLoaded = false;
  private cachedProjectData: any = null;

  setProject(project: any): void {
    try {
      if (!project) {
        throw new Error('Invalid project configuration');
      }

      // Reset multi-manufacturing-method Results page bookkeeping — a new
      // project load starts with no active child selected; the Results page
      // will auto-pick the first Done child in its ngOnInit.
      this.activeResultsChildId = null;

      if (project.name) {
        this.project.name = project.name;
      }

      if (project.status) {
        this.project.status = project.status;
      }

      if (project.sharing) {
        this.sharing = project.sharing;
      }

      if (project.images) {
        Object.assign(this.projectImages, project.images);
      }

      const projectType = project.projectType || this.project.projectType;

      // For SoftJaws projects, only load minimal config
      if (projectType === ProjectType.SoftJaws) {
        if (this.project.status === 4) {
          this.softJawsResultUrl = project.optimization?.softjaws_result;
        }
        Object.assign(this.project, {
          flowVersion: project.config?.flowVersion,
          runType: project.config?.runType,
          projectType: projectType,
          partNames: project.config?.partNames || {},
          partVisibility: project.config?.partVisibility || {},
          softJaws: project.config?.softJaws || this.project.softJaws,
          unitSystem: project.config?.unitSystem ?? 'metric'
        });
      } else if (projectType === ProjectType.Mold) {
        // Deep-merge saved mold config with defaults so missing fields
        // (e.g. wall_thickness on older projects) get sensible values.
        const savedMold = project.config?.mold;
        const mergedMold = savedMold
          ? {
              ...DEFAULT_MOLD_CONFIG,
              ...savedMold,
              mold: { ...DEFAULT_MOLD_CONFIG.mold, ...savedMold.mold },
              ribs: {
                ...DEFAULT_MOLD_CONFIG.ribs,
                ...savedMold.ribs,
                manufacturing: { ...DEFAULT_MOLD_CONFIG.ribs.manufacturing, ...savedMold.ribs?.manufacturing },
                grids: savedMold.ribs?.grids ?? DEFAULT_MOLD_CONFIG.ribs.grids,
              },
              bosses: {
                ...DEFAULT_MOLD_CONFIG.bosses,
                ...savedMold.bosses,
                common: { ...DEFAULT_MOLD_CONFIG.bosses.common, ...savedMold.bosses?.common },
                list: savedMold.bosses?.list ?? DEFAULT_MOLD_CONFIG.bosses.list,
              },
            }
          : this.project.mold;
        Object.assign(this.project, {
          projectType: projectType,
          mold: mergedMold,
          partNames: project.config?.partNames || {},
          partTypes: project.config?.partTypes || this.project.partTypes,
          partVisibility: project.config?.partVisibility || {},
          unitSystem: project.config?.unitSystem ?? 'metric'
        });
      } else {
        // For Topology Optimization projects, load full config
        console.log('[ProjectConfig] setProject — topology optimization');
        console.log('[ProjectConfig] project.optimization present:', !!project.optimization);
        console.log('[ProjectConfig] project.config?.flowVersion:', project.config?.flowVersion);
        console.log('[ProjectConfig] project.config?.loadcases count:', project.config?.loadcases?.length);

        // Lift legacy single-method config into the new-shape manufacturingMethods[]
        // array if the backend payload doesn't already have it. Leaves the legacy
        // flat fields in place (V2 mirror strategy — see design doc §9).
        // Ensure manufacturingMethods[] exists even for brand-new projects
        // that have no config yet (backend returns config: null/{}).
        project.config = normalizeToNewShape(project.config ?? {} as any);

        Object.assign(this.project, {
          optimization: project.optimization || this.project.optimization,
          resolution: project.config?.resolution || this.project.resolution,
          symmetry: project.config?.symmetry || this.project.symmetry,
          manufacturingMethod: project.config?.manufacturingMethod || this.project.manufacturingMethod,
          manufacturingMethods: project.config?.manufacturingMethods || this.project.manufacturingMethods,
          masterProjectId: project.masterProjectId ?? this.project.masterProjectId,
          methodId: project.methodId ?? this.project.methodId,
          childProjectIds: project.childProjectIds ?? this.project.childProjectIds,
          runSummary: project.runSummary ?? this.project.runSummary,
          millingVector: project.config?.millingVector || this.project.millingVector,
          drawDirectionVector: project.config?.drawDirectionVector || this.project.drawDirectionVector,
          ribsDensity: project.config?.ribsDensity || this.project.ribsDensity,
          wallThickness: project.config?.wallThickness || this.project.wallThickness,
          bottomCutFrac: project.config?.bottomCutFrac || this.project.bottomCutFrac,
          materials: project.config?.materials?.map((item: any) => Material.fromJSON(item)) || this.project.materials,
          parts: {
            design: project.config?.parts?.design || this.project.parts.design,
            nondesign: project.config?.parts?.nondesign || this.project.parts.nondesign
          },
          partNames: project.config?.partNames || this.project.partNames,
          partMaterials: project.config?.partMaterials || this.project.partMaterials,
          partVisibility: project.config?.partVisibility || this.project.partVisibility,
          loadcases:
            project.config?.loadcases?.map((item: any) => LoadCaseItem.fromJSON(item)) || this.project.loadcases,
          goal: {
            ...this.project.goal,
            fractionOfMaterial: project.config?.goal?.fractionOfMaterial || this.project.goal.fractionOfMaterial,
            massTarget: project.config?.goal?.massTarget || this.project.goal.massTarget,
            objective: project.config?.goal?.objective || this.project.goal.objective,
            frequencyConstraint: project.config?.goal?.frequencyConstraint || 200
          },
          minimumFeatureSize: project.config?.minimumFeatureSize || this.project.minimumFeatureSize,
          minimumToolSize: project.config?.minimumToolSize || this.project.minimumToolSize,
          minimumToolSizeUnit: project.config?.minimumToolSizeUnit || this.project.minimumToolSizeUnit,
          userCoordinateSystem: project.config?.userCoordinateSystem || this.project.userCoordinateSystem,
          generateOrganic: project.config?.generateOrganic ?? this.project.generateOrganic,
          generatePrismatic: project.config?.generatePrismatic ?? this.project.generatePrismatic,
          skeletonMethod: project.config?.skeletonMethod || this.project.skeletonMethod,
          stressConstraintEnabled:
            project.config?.stressConstraintEnabled ?? this.project.stressConstraintEnabled,
          deformationConstraintEnabled:
            project.config?.deformationConstraintEnabled ?? this.project.deformationConstraintEnabled,
          dynamicConstraintType:
            project.config?.dynamicConstraintType ?? this.project.dynamicConstraintType,
          flowVersion: project.config?.flowVersion || this.project.flowVersion,
          runType: project.config?.runType || this.project.runType,
          projectType: projectType,
          costModel: project.config?.costModel || this.project.costModel,
          numberOfPlanes: project.config?.numberOfPlanes ?? this.project.numberOfPlanes,
          extrusionPlaneGeneration:
            project.config?.extrusionPlaneGeneration ?? this.project.extrusionPlaneGeneration,
          solverParameters: project.config?.solverParameters ?? this.project.solverParameters,
          unitSystem: project.config?.unitSystem ?? 'metric',
          densityUnit: project.config?.densityUnit ?? 'kg/m³',
          massUnit: project.config?.massUnit ?? 'kg'
        });
      }

      try {
        this.downloadLinks = (project as any).download;
        this.bdfJob = (project as any).bdfJob ?? null;
      } catch (e) {}
    } catch (e: any) {
      console.error('Error setting project:', e.message);
    }

    try {
      this.setTabTitle(this.project.name);
    } catch (e) {}
  }

  setTabTitle(name: string): void {
    const prefix = environment.PRODUCTION ? '' : `(${environment.ENV_LABEL}) `;
    document.title = name ? `${prefix}${name} - InfinitForm` : `${prefix}InfinitForm`;
  }

  // ─── Multi-manufacturing-method: case management ───

  /**
   * Read the current flat scratch-pad fields on `this.project` into a
   * FlatManufacturingFields snapshot — used by `commitActiveCaseToArray`.
   */
  private readFlatSnapshot(): FlatManufacturingFields {
    return {
      manufacturingMethod: this.project.manufacturingMethod,
      millingVector: { ...this.project.millingVector },
      drawDirectionVector: { ...this.project.drawDirectionVector },
      generatePrismatic: this.project.generatePrismatic,
      costModel: this.project.costModel ? { ...this.project.costModel } : undefined,
      minimumToolSize: this.project.minimumToolSize,
      minimumToolSizeUnit: this.project.minimumToolSizeUnit,
      ribsDensity: this.project.ribsDensity,
      wallThickness: this.project.wallThickness,
      bottomCutFrac: this.project.bottomCutFrac,
      skeletonMethod: this.project.skeletonMethod,
    };
  }

  /**
   * Copy the current flat scratch-pad fields into `manufacturingMethods[activeIndex]`,
   * recomputing the case's label from the captured direction flags. Called
   * before switching active case and before saving the project.
   *
   * No-op if the project has no manufacturingMethods[] (legacy path) or the
   * active id doesn't resolve to a case.
   */
  commitActiveCaseToArray(activeMethodId: string | null): void {
    const cases = this.project.manufacturingMethods;
    if (!cases || cases.length === 0) return;
    const idx = activeMethodId
      ? cases.findIndex((c) => c.methodId === activeMethodId)
      : 0;
    if (idx < 0) return;

    const captured = captureActiveCaseFromFlat(cases[idx], this.readFlatSnapshot());
    captured.label = computeCaseLabel(captured);
    cases[idx] = captured;
  }

  /**
   * Copy `manufacturingMethods[targetCase]` into the flat scratch-pad on
   * `this.project`, so the optimization sub-blocks immediately reflect the
   * selected case. Called after `activeMethodId` changes.
   */
  hydrateFlatFromCase(methodId: string | null): void {
    const cases = this.project.manufacturingMethods;
    if (!cases || cases.length === 0) return;
    const target = methodId
      ? cases.find((c) => c.methodId === methodId)
      : cases[0];
    if (!target) return;

    Object.assign(this.project, projectFlatFromCase(target));
  }

  /**
   * Add a new manufacturing case to the project. Returns the new case's
   * methodId, or null if the addition was rejected (AM already present,
   * max cases reached).
   *
   * The caller is responsible for also calling `commitActiveCaseToArray`
   * with the OLD active id before invoking this, and for updating the
   * SessionService.activeMethodId pointer to the returned id.
   */
  addCase(method: ManufacturingMethod, maxCases = 4): string | null {
    if (!this.project.manufacturingMethods) {
      this.project.manufacturingMethods = [];
    }
    const cases = this.project.manufacturingMethods;
    if (cases.length >= maxCases) return null;
    if (!canAddMethod(method, cases)) return null;

    const methodId = this.generateMethodId();
    cases.push(makeDefaultCase(methodId, method));
    return methodId;
  }

  /**
   * Remove a case from the project. Refuses to delete the last remaining
   * case. Returns true if the removal happened.
   *
   * If the removed case was the active one, the caller is responsible for
   * picking a new active case (typically the first remaining, or null if the
   * list is now empty) and calling `hydrateFlatFromCase` to update the
   * scratch-pad. `SessionService.removeManufacturingCase` already does this.
   *
   * Empty result is allowed: new projects start with no manufacturing methods
   * (the user picks one before optimization is enabled — see canOptimize).
   */
  removeCase(methodId: string): boolean {
    const cases = this.project.manufacturingMethods;
    if (!cases || cases.length === 0) return false;
    const idx = cases.findIndex((c) => c.methodId === methodId);
    if (idx < 0) return false;
    cases.splice(idx, 1);
    return true;
  }

  private generateMethodId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
      const r = (Math.random() * 16) | 0;
      const v = ch === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Multi-manufacturing-method: which child's results are currently shown on
   * the Results page. Null when:
   *  - the project isn't a master (legacy single-project)
   *  - no child has been selected yet
   *  - the master has no finished children to show
   *
   * Set by `mergeChildOptimizationIntoMaster` below. Used by the Results page's
   * case list to highlight the active case.
   */
  public activeResultsChildId: string | null = null;

  /**
   * Fetch a single child project's full document (including the
   * `optimization` URL block and materials array) and merge it into
   * `this.project.optimization` + `this.project.materials`. Also sets
   * `activeResultsChildId` so the Results page knows which case is active.
   *
   * Pure transport — does NOT touch the 3D engine. Callers are responsible
   * for tearing down the old mesh and reloading the new one.
   *
   * Returns the child's backend document, or null on failure.
   */
  async mergeChildOptimizationIntoMaster(
    childId: string,
  ): Promise<Record<string, unknown> | null> {
    const base = this.other_user_id ? `?other_user_id=${this.other_user_id}` : '';
    try {
      const childDoc = await lastValueFrom(
        this.http.get<Record<string, unknown>>(
          `${environment.API_ADDRESS}/project/${childId}${base}`,
        ),
      );
      if (!childDoc) return null;

      const optimization = (childDoc as { optimization?: unknown }).optimization;
      if (optimization) {
        (this.project as { optimization?: unknown }).optimization = optimization;
      }

      // Propagate the child's flowVersion so the v2 results code path
      // (PLY + JSON loadcases) activates. The master's own flowVersion
      // stays at 1 because it never ran — the child has the real value.
      const childFlowVersion = (childDoc as { config?: { flowVersion?: number } })
        .config?.flowVersion;
      if (childFlowVersion && childFlowVersion > (this.project.flowVersion ?? 0)) {
        this.project.flowVersion = childFlowVersion;
      }

      // Child inherits materials from master at spawn time, but in case a
      // material override ever lands per-child, prefer the child's copy.
      const childConfig = (childDoc as { config?: { materials?: unknown[] } })
        .config;
      if (childConfig?.materials && Array.isArray(childConfig.materials)) {
        (this.project as { materials?: unknown }).materials =
          childConfig.materials.map((item: unknown) =>
            Material.fromJSON(item),
          );
      }

      this.activeResultsChildId = childId;
      return childDoc;
    } catch (err) {
      console.error(
        `[ProjectConfig] Failed to merge child optimization for ${childId}:`,
        err,
      );
      return null;
    }
  }

  /**
   * Fetch a lightweight summary of every child project for the currently
   * loaded master — one `GET /project/:id` per id listed in
   * `this.project.childProjectIds`, in parallel.
   *
   * Returns a minimal shape `{id, methodId, label, status, status_progress, image}`
   * per child. Used by the Compare View to populate tiles before the modal
   * does its own richer per-child fetch for mesh URLs + material density.
   *
   * Returns an empty array when the project has no children.
   */
  async fetchMasterChildren(): Promise<
    Array<{
      id: string;
      methodId: string | null;
      label: string;
      status: number;
      status_progress: number;
      image: string | null;
    }>
  > {
    const childIds = (this.project as { childProjectIds?: string[] })
      .childProjectIds;
    if (!childIds || childIds.length === 0) return [];

    // Resolve labels from the master's canonical manufacturingMethods[] array
    // (child.methodId → label). Fallback to method name if no label.
    const labelByMethodId = new Map<string, string>();
    const cases =
      (this.project as { manufacturingMethods?: Array<{ methodId: string; method: string; label?: string }> })
        .manufacturingMethods ?? [];
    for (const c of cases) {
      labelByMethodId.set(c.methodId, c.label ?? String(c.method));
    }

    const base = this.other_user_id ? `?other_user_id=${this.other_user_id}` : '';
    const docs = await Promise.all(
      childIds.map((id) =>
        lastValueFrom(
          this.http.get<any>(`${environment.API_ADDRESS}/project/${id}${base}`),
        ).then((doc) => doc ? { ...doc, _childId: id } : null)
         .catch(() => null),
      ),
    );

    return docs
      .filter((doc): doc is Record<string, unknown> => !!doc)
      .map((doc) => {
        const methodId =
          typeof (doc as { methodId?: unknown }).methodId === 'string'
            ? ((doc as { methodId: string }).methodId)
            : null;
        return {
          // Cloud findOneForGUI doesn't return id/_id — use the child ID
          // we requested with. Desktop NeDB docs do return id.
          id: String(
            (doc as { id?: unknown }).id ??
            (doc as { _id?: unknown })._id ??
            (doc as { _childId?: unknown })._childId,
          ),
          methodId,
          label:
            (methodId && labelByMethodId.get(methodId)) ??
            ((doc as { name?: string }).name ?? ''),
          status: Number((doc as { status?: unknown }).status ?? 0),
          status_progress: Number((doc as { status_progress?: unknown }).status_progress ?? 0),
          image:
            typeof (doc as { image?: unknown }).image === 'string'
              ? ((doc as { image: string }).image)
              : null,
        };
      });
  }

  async getProject(): Promise<any> {
    // Return cached project data if already loaded
    if (this.projectLoaded && this.cachedProjectData) {
      return this.cachedProjectData;
    }

    this.progressService.updateProgress(10, 'Loading project data...');

    let url = `${environment.API_ADDRESS}/project/${this.project_id}`;
    if (this.other_user_id) url += `?other_user_id=${this.other_user_id}`;

    const result: any = await lastValueFrom(this.http.get(url));
    this.progressService.updateProgress(15, 'Project data loaded');

    // Cache the project data
    this.cachedProjectData = result;
    this.projectLoaded = true;

    // Set example project flag
    this.isExampleProject = result?.isExample === true;

    return result;
  }

  async copyExampleProject(): Promise<{ message: string } | null> {
    if (!this.project_id || !this.isExampleProject || this.copyingExampleProject) {
      return null;
    }

    this.copyingExampleProject = true;

    try {
      const result = await lastValueFrom(
        this.http.post<{ message: string }>(`${environment.API_ADDRESS}/example-projects/${this.project_id}/copy`, {})
      );
      return result;
    } catch (error) {
      console.error('Error copying example project:', error);
      throw error;
    } finally {
      this.copyingExampleProject = false;
    }
  }
}
